import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  styled,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ContactsIcon from '@mui/icons-material/Contacts';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import firebaseDataService from '../../services/FirebaseDataService';
import SuperAdminService from '../../services/SuperAdminService';
import SuperAdminLayout from './SuperAdminLayout';

// Tablo için bir arayüz tanımlayalım
interface ContactFormData {
  id: string;
  nameSurname: string;
  email: string;
  phone: string;
  createdAt: string;
}

// Kaydırılabilir içerik alanı
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  height: 'calc(100vh - 150px)',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

// Başlık alanı
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const SuperAdminContacts: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ContactFormData[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<ContactFormData[]>([]);
  
  // Arama için state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Silme işlemi için state
  const [contactToDelete, setContactToDelete] = useState<ContactFormData | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const checkSuperAdminAccess = async () => {
      try {
        setLoading(true);
        
        // Kullanıcının rolünü kontrol et
        if (!currentUser) {
          navigate('/login');
          return;
        }
        
        const userData = await firebaseDataService.getUserData(currentUser.uid);
        
        if (!userData || userData.role !== 'superadmin') {
          console.log('Kullanıcı süper admin değil');
          navigate('/login');
          return;
        }
        
        // İletişim verilerini yükle
        loadContactFormData();
      } catch (error) {
        console.error('Yetkilendirme kontrolü sırasında hata:', error);
        setError('Yetkilendirme sırasında bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    checkSuperAdminAccess();
  }, [currentUser, navigate]);

  // İletişim formları verilerini yükleme
  const loadContactFormData = async () => {
    try {
      setLoading(true);
      
      // API üzerinden iletişim mesajlarını al
      const superAdminService = SuperAdminService.getInstance();
      const contactsList = await superAdminService.getAllContacts();
      
      setContacts(contactsList);
      setFilteredContacts(contactsList);
      setError(null);
    } catch (error) {
      console.error('İletişim verileri yüklenirken hata:', error);
      setError('İletişim formu verileri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Arama fonksiyonu
  useEffect(() => {
    if (contacts.length === 0) {
      setFilteredContacts([]);
      return;
    }
    
    if (searchTerm.trim() === '') {
      setFilteredContacts(contacts);
      return;
    }
    
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const results = contacts.filter(contact => 
      contact.nameSurname.toLowerCase().includes(lowerCaseSearchTerm) ||
      contact.email.toLowerCase().includes(lowerCaseSearchTerm) ||
      contact.phone.includes(searchTerm)
    );
    
    setFilteredContacts(results);
    setPage(0); // Arama yapıldığında ilk sayfaya dön
  }, [searchTerm, contacts]);

  // Sayfalandırılmış öğeler
  const paginatedContacts = filteredContacts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Tarihi formatla
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Silme işlemleri
  const handleDeleteClick = (contact: ContactFormData) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setContactToDelete(null);
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete) return;
    
    setIsDeleting(true);
    try {
      // API üzerinden sil
      const superAdminService = SuperAdminService.getInstance();
      await superAdminService.deleteContact(contactToDelete.id);
      
      setError(null);
      // Silme başarılı olduğunda dialog kapanır ve listeyi yeniden yükle
      handleCloseDeleteDialog();
      await loadContactFormData();
    } catch (error) {
      console.error("Mesajı silerken hata oluştu:", error);
      setError('Mesaj silinirken bir hata oluştu.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <SuperAdminLayout title="İletişim Listesi" icon={<ContactsIcon />}>
      {/* Başlık ve Arama Alanı */}
      <HeaderContainer>
        <Typography variant="h5" component="h1" fontWeight="bold">
          İletişim Formu Mesajları
        </Typography>
        
        <TextField
          size="small"
          placeholder="İsim, E-posta veya Telefon Ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 300 } }}
        />
      </HeaderContainer>
      
      {/* Hata Mesajı */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Ana İçerik - Tablo */}
      <ScrollableContent>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px' 
          }}>
            <CircularProgress />
          </Box>
        ) : filteredContacts.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <ContactsIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.7, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Henüz İletişim Formu Gönderimi Bulunmuyor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? "Arama kriterlerinize uygun sonuç bulunamadı." : "İletişim formundan gönderilen mesajlar burada listelenecektir."}
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="medium" stickyHeader sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        backgroundColor: 'primary.main' 
                      }}
                    >
                      Ad Soyad
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        backgroundColor: 'primary.main' 
                      }}
                    >
                      E-posta
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        backgroundColor: 'primary.main' 
                      }}
                    >
                      Telefon
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        backgroundColor: 'primary.main' 
                      }}
                    >
                      Gönderim Tarihi
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 'bold', 
                        backgroundColor: 'primary.main',
                        width: '80px'
                      }}
                      align="center"
                    >
                      İşlemler
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedContacts.map((contact) => (
                    <TableRow 
                      key={contact.id}
                      hover
                      sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                    >
                      <TableCell>{contact.nameSurname}</TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.phone}</TableCell>
                      <TableCell>{formatDate(contact.createdAt)}</TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          color="error"
                          title="Sil"
                          onClick={() => handleDeleteClick(contact)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Sayfalama */}
            <TablePagination
              component="div"
              count={filteredContacts.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Sayfa başına satır:"
              labelDisplayedRows={({ from, to, count }) => `${count} kayıttan ${from}-${to} arası gösteriliyor`}
            />
          </Paper>
        )}
      </ScrollableContent>

      {/* Silme Onay Dialogu */}
      <Dialog
        open={deleteDialogOpen}
        onClose={!isDeleting ? handleCloseDeleteDialog : undefined}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Mesajı Sil
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {contactToDelete && (
              <>
                <strong>{contactToDelete.nameSurname}</strong> tarafından gönderilen mesajı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteDialog} 
            color="primary"
            disabled={isDeleting}
          >
            İptal
          </Button>
          <Button 
            onClick={handleDeleteContact} 
            color="error" 
            autoFocus
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </SuperAdminLayout>
  );
};

export default SuperAdminContacts;
